(function initialiseArtboxQuickShopLifecycle() {
  const lifecycleKey = Symbol.for('artbox.quickShop.lifecycle');
  const existingLifecycle = window[lifecycleKey];

  if (existingLifecycle) {
    existingLifecycle.initialise(document);
    return;
  }

  const rememberedOptions = new Map([
    ['device', 'preferredDeviceModel'],
    ['case type', 'preferredCaseType'],
  ]);
  const pendingAttribute = 'data-artbox-quick-shop-pending';
  const pendingTimeoutMs = 6000;
  const visualAssetTimeoutMs = 250;
  const records = new WeakMap();
  let optionSequence = 0;
  let pendingTimeout = null;
  let pendingSelection = null;

  function clearPendingState() {
    window.clearTimeout(pendingTimeout);
    pendingTimeout = null;
    pendingSelection = null;
    document.documentElement.removeAttribute(pendingAttribute);
  }

  function releaseStalledPendingState() {
    const root = document.querySelector('[data-artbox-quick-shop]');
    if (root && root.dataset.artboxQuickShopReady !== 'true') {
      root.closest('.t4s-modal')?.querySelector('[data-t4s-modal-close], .t4s-modal-close')?.click();
    }
    clearPendingState();
  }

  function singleDevice(values) {
    return values.length === 1 ? values[0].trim() : '';
  }

  function triggerSelection(trigger) {
    let card = trigger.hasAttribute('data-artbox-case-type')
      ? trigger : trigger.closest('[data-artbox-case-type]');
    const caseType = card?.dataset.artboxCaseType?.trim() || '';
    if (!caseType) {
      card = trigger.closest('[data-artbox-linked-card]');
      if (card?.dataset.artboxCardContext !== 'search') return null;
    }

    let device = '';
    try {
      const target = new URL(trigger.getAttribute('href'), window.location.href);
      device = singleDevice(target.searchParams.getAll('artbox_device'));
    } catch (_error) {
      // A malformed link can still carry a confirmed device on its card.
    }
    if (!device && caseType && card.dataset.artboxDeviceConfirmed === 'true') {
      device = card.dataset.artboxDevice?.trim() || '';
    }
    if (!device) {
      try {
        const current = new URL(window.location.href);
        device = singleDevice([
          ...current.searchParams.getAll('filter.v.option.device'),
          ...current.searchParams.getAll('filter.p.option.device'),
        ]);
      } catch (_error) {
        // Without one confirmed model, the Device choice remains required.
      }
    }
    return { caseType, device };
  }

  function beginPendingState(trigger) {
    pendingSelection = triggerSelection(trigger);
    window.clearTimeout(pendingTimeout);
    document.documentElement.setAttribute(pendingAttribute, 'true');
    pendingTimeout = window.setTimeout(releaseStalledPendingState, pendingTimeoutMs);
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function readRememberedValue(storageKey) {
    try {
      return window.localStorage.getItem(storageKey);
    } catch (_error) {
      return null;
    }
  }

  function writeRememberedValue(storageKey, value) {
    try {
      window.localStorage.setItem(storageKey, value);
    } catch (_error) {
      return;
    }
  }

  function waitForKallesUpdate() {
    return new Promise((resolve) => {
      const schedule = typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => callback();
      schedule(() => schedule(resolve));
    });
  }

  function waitForImage(image) {
    if (image.complete) {
      return typeof image.decode === 'function'
        ? image.decode().catch(() => undefined)
        : Promise.resolve();
    }
    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }

  async function waitForVisualAssets(root) {
    const image = root.querySelector('.artbox-quick-shop__image');
    const requests = image ? [waitForImage(image)] : [];
    if (!requests.length) return;

    let timeout;
    await Promise.race([
      Promise.allSettled(requests),
      new Promise((resolve) => {
        timeout = window.setTimeout(resolve, visualAssetTimeoutMs);
      }),
    ]);
    window.clearTimeout(timeout);
  }

  function optionName(option) {
    return option.dataset.artboxOptionName
      || option.querySelector('.t4s-swatch__title')?.textContent?.split(':')[0]
      || '';
  }

  function optionItems(option) {
    return Array.from(option.querySelectorAll('[data-swatch-item]'));
  }

  function selectedItem(option) {
    return option.querySelector('[data-swatch-item].is--selected');
  }

  function isAvailable(item) {
    return Boolean(item)
      && !item.classList.contains('is--soldout')
      && !item.classList.contains('is--unavailable')
      && item.getAttribute('aria-disabled') !== 'true'
      && !item.disabled;
  }

  function selectedVariantIsAvailable(root) {
    const selectedOption = root.querySelector('select[name="id"] option:checked');
    return !selectedOption
      || (!selectedOption.disabled && !selectedOption.classList.contains('is--sold-out'));
  }

  function syncSummaryImage(root) {
    const image = root.querySelector('.artbox-quick-shop__image');
    const selected = root.querySelector('select[name="id"] option:checked');
    const url = selected?.dataset.artboxImageUrl;
    if (!image || !url || image.getAttribute('src') === url) return;
    image.removeAttribute('srcset');
    image.src = url;
  }

  function syncAddToBagLabel(root) {
    const label = root.querySelector('[data-atc-form] .t4s-btn-atc_text');
    const nativeLabel = root.dataset.artboxLabelAddToCart;
    const bagLabel = root.dataset.artboxLabelAddToBag;
    if (!label || !nativeLabel || !bagLabel) return;
    if ([nativeLabel, bagLabel].includes(label.textContent.trim())) label.textContent = bagLabel;
  }

  function syncButtonRequirement(root) {
    const requiredOption = root.querySelector('[data-artbox-option-state="required"]');
    const button = root.querySelector('[data-atc-form]');
    if (!button) return;

    if (requiredOption) {
      if (button.dataset.artboxRequirementManaged !== 'true') {
        button.dataset.artboxRequirementManaged = 'true';
        button.dataset.artboxOriginalDisabled = String(button.disabled);
        button.dataset.artboxOriginalAriaDisabled = button.getAttribute('aria-disabled') || '';
      }
      root.dataset.artboxChoiceRequired = normalize(optionName(requiredOption));
      button.dataset.artboxChoiceRequired = 'true';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    delete root.dataset.artboxChoiceRequired;
    if (button.dataset.artboxRequirementManaged !== 'true') return;

    const originallyDisabled = button.dataset.artboxOriginalDisabled === 'true';
    const originalAriaDisabled = button.dataset.artboxOriginalAriaDisabled;
    button.disabled = originallyDisabled || !selectedVariantIsAvailable(root);
    if (originalAriaDisabled) {
      button.setAttribute('aria-disabled', originalAriaDisabled);
    } else if (!button.disabled) {
      button.removeAttribute('aria-disabled');
    }
    delete button.dataset.artboxChoiceRequired;
    delete button.dataset.artboxRequirementManaged;
    delete button.dataset.artboxOriginalDisabled;
    delete button.dataset.artboxOriginalAriaDisabled;
  }

  function labelForState(root, option, state) {
    if (state === 'required') return root.dataset.artboxLabelChooseDevice || '';
    if (state !== 'collapsed') return '';
    if (normalize(optionName(option)) === 'case type') {
      return root.dataset.artboxLabelCompatible || '';
    }
    return root.dataset.artboxLabelCurrent || '';
  }

  function setOptionState(root, option, state) {
    option.dataset.artboxOptionState = state;
    const toggle = option.querySelector('[data-artbox-option-toggle]');
    if (toggle) {
      const collapsed = state === 'collapsed';
      toggle.textContent = collapsed
        ? root.dataset.artboxLabelChange || 'Change'
        : root.dataset.artboxLabelDone || 'Done';
      toggle.setAttribute('aria-expanded', String(!collapsed));
    }

    const note = option.querySelector('[data-artbox-option-note]');
    if (note) {
      const label = labelForState(root, option, state);
      note.textContent = label;
      note.hidden = !label;
    }
  }

  function decorateOption(root, option) {
    if (option.dataset.artboxQuickShopEnhanced === 'true') return;
    option.dataset.artboxQuickShopEnhanced = 'true';

    const name = optionName(option);
    const normalizedName = normalize(name);
    const storageKey = rememberedOptions.get(normalizedName);
    const items = optionItems(option);

    if (!storageKey) {
      setOptionState(root, option, items.length <= 1 ? 'identity' : 'expanded');
      return;
    }

    const title = option.querySelector('.t4s-swatch__title');
    const current = title?.querySelector('[data-current-value]');
    const titleContent = title?.querySelector(':scope > span') || title;
    if (title && current && titleContent) {
      title.classList.add('artbox-quick-shop__option-title');
      for (const node of Array.from(titleContent.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) node.remove();
      }
      const label = document.createElement('span');
      label.className = 'artbox-quick-shop__option-label';
      label.textContent = name;
      current.classList.add('artbox-quick-shop__option-value');
      titleContent.classList.add('artbox-quick-shop__selection');
      titleContent.insertBefore(label, current);

      const list = option.querySelector('[data-swatch-list], .t4s-swatch__list');
      if (list && !list.id) {
        optionSequence += 1;
        list.id = `ArtboxQuickShopOption-${optionSequence}`;
      }

      if (normalizedName === 'device') {
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.dataset.artboxOptionToggle = '';
        toggle.className = 'artbox-quick-shop__option-toggle';
        if (list?.id) toggle.setAttribute('aria-controls', list.id);
        title.appendChild(toggle);
      }

      const note = document.createElement('span');
      note.dataset.artboxOptionNote = '';
      note.className = 'artbox-quick-shop__option-note';
      note.hidden = true;
      title.after(note);
    }
  }

  function finishSheetTransition(record, state) {
    const sheet = record.sheet;
    if (!sheet || sheet.transitionState !== state) return;

    window.clearTimeout(sheet.transitionFallback);
    sheet.transitionFallback = null;
    sheet.transitionState = null;
    if (state === 'closing') {
      sheet.close.click();
      return;
    }

    sheet.inner.dataset.artboxQuickShopSheetState = 'open';
    sheet.inner.style.removeProperty('--artbox-quick-shop-drag-y');
  }

  function settleSheet(record, state) {
    const sheet = record.sheet;
    if (!sheet) return;

    sheet.start = null;
    sheet.transitionState = state;
    sheet.inner.dataset.artboxQuickShopSheetState = state;
    const targetY = state === 'closing'
      ? Math.max(sheet.inner.getBoundingClientRect().height, window.innerHeight)
      : 0;

    // Flush the current dragged transform before applying the transition target.
    sheet.inner.getBoundingClientRect();
    sheet.inner.style.setProperty('--artbox-quick-shop-drag-y', `${Math.round(targetY)}px`);
    window.clearTimeout(sheet.transitionFallback);
    sheet.transitionFallback = window.setTimeout(() => finishSheetTransition(record, state), 320);
  }

  function enhanceBottomSheet(root, record) {
    if (record.sheet || !window.matchMedia?.('(max-width: 767px)').matches) return;
    const inner = root.closest('.t4s-modal__inner');
    const modal = inner?.closest('.t4s-modal');
    const close = modal?.querySelector('[data-t4s-modal-close], .t4s-modal-close');
    if (!inner || !close) return;

    const handle = document.createElement('div');
    handle.className = 'artbox-quick-shop__drag-handle';
    handle.dataset.artboxQuickShopDragHandle = '';
    handle.setAttribute('aria-hidden', 'true');
    inner.prepend(handle);

    const sheet = {
      close,
      handle,
      inner,
      start: null,
      transitionFallback: null,
      transitionState: null,
    };
    record.sheet = sheet;
    inner.dataset.artboxQuickShopSheetState = 'open';

    inner.addEventListener('transitionend', (event) => {
      if (event.target !== inner || event.propertyName !== 'transform' || !sheet.transitionState) return;
      finishSheetTransition(record, sheet.transitionState);
    });

    handle.addEventListener('pointerdown', (event) => {
      if (event.isPrimary === false || sheet.transitionState === 'closing') return;
      window.clearTimeout(sheet.transitionFallback);
      sheet.transitionFallback = null;
      sheet.transitionState = null;
      sheet.start = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: window.performance.now(),
      };
      inner.dataset.artboxQuickShopSheetState = 'dragging';
      if (!inner.style.getPropertyValue('--artbox-quick-shop-drag-y')) {
        inner.style.setProperty('--artbox-quick-shop-drag-y', '0px');
      }
      try {
        handle.setPointerCapture(event.pointerId);
      } catch (_error) {
        // Synthetic test events and older browsers can reject pointer capture.
      }
    });

    handle.addEventListener('pointermove', (event) => {
      if (!sheet.start || event.pointerId !== sheet.start.id) return;
      const deltaY = Math.max(0, event.clientY - sheet.start.y);
      inner.style.setProperty('--artbox-quick-shop-drag-y', `${Math.round(deltaY)}px`);
    });

    handle.addEventListener('pointerup', (event) => {
      if (!sheet.start || event.pointerId !== sheet.start.id) return;
      const deltaX = Math.abs(event.clientX - sheet.start.x);
      const deltaY = Math.max(0, event.clientY - sheet.start.y);
      const elapsed = Math.max(1, window.performance.now() - sheet.start.time);
      const closeDistance = Math.min(112, Math.max(72, inner.getBoundingClientRect().height * .22));
      const fastDownwardFlick = elapsed >= 16 && deltaY >= 32 && deltaY / elapsed >= .65;
      const shouldClose = deltaY > deltaX && (deltaY >= closeDistance || fastDownwardFlick);
      settleSheet(record, shouldClose ? 'closing' : 'settling');
    });

    handle.addEventListener('pointercancel', () => settleSheet(record, 'settling'));
  }

  async function restoreOption(root, option, record) {
    const normalizedName = normalize(optionName(option));
    const storageKey = rememberedOptions.get(normalizedName);
    if (!storageKey) return;

    const targetValue = record.selection
      ? (normalizedName === 'device' ? record.selection.device : record.selection.caseType)
      : readRememberedValue(storageKey);
    const exactTarget = targetValue
      ? optionItems(option).find((item) => item.getAttribute('data-value')?.trim() === targetValue && isAvailable(item))
      : null;

    if (exactTarget) {
      if (!exactTarget.classList.contains('is--selected')) {
        record.restoring = true;
        exactTarget.click();
        await waitForKallesUpdate();
        record.restoring = false;
      }
      setOptionState(root, option, normalizedName === 'device' ? 'collapsed' : 'expanded');
      return;
    }

    setOptionState(root, option, normalizedName === 'device' ? 'required' : 'expanded');
  }

  async function enhanceOptions(root, record) {
    if (!root.classList.contains('initProducts__enabled')) return;
    if (record.enhancing) return record.enhancing;

    record.enhancing = (async () => {
      const allOptions = Array.from(root.querySelectorAll('[data-swatch-option]'));
      allOptions.forEach((option) => decorateOption(root, option));

      // The chosen Case Type changes which Device values Kalles makes available.
      const restorationOrder = record.selection ? ['case type', 'device'] : ['device', 'case type'];
      for (const name of restorationOrder) {
        const option = allOptions.find((candidate) => normalize(optionName(candidate)) === name);
        if (option && option.dataset.artboxOptionRestored !== 'true') {
          option.dataset.artboxOptionRestored = 'true';
          await restoreOption(root, option, record);
        }
      }

      syncButtonRequirement(root);
      syncSummaryImage(root);
      syncAddToBagLabel(root);
      if (!record.visualsReady) record.visualsReady = waitForVisualAssets(root);
      await record.visualsReady;
      root.dataset.artboxQuickShopReady = 'true';
      clearPendingState();
    })();

    try {
      await record.enhancing;
    } finally {
      record.enhancing = null;
    }
  }

  function nodeContainsSwatchOption(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    return node.matches('[data-swatch-option]')
      || Boolean(node.querySelector('[data-swatch-option]'));
  }

  function mutationTouchesSwatchOptions(mutation) {
    return [...mutation.addedNodes, ...mutation.removedNodes].some(nodeContainsSwatchOption);
  }

  function scheduleEnhancement(root, record) {
    if (record.enhancementScheduled) return;
    record.enhancementScheduled = true;
    const schedule = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : (callback) => callback();

    schedule(async () => {
      record.enhancementScheduled = false;
      if (!root.isConnected) return;
      if (record.enhancing) await record.enhancing;
      if (root.isConnected) enhanceOptions(root, record);
    });
  }

  function initialiseRoot(root) {
    if (records.has(root)) return;

    const record = {
      root,
      selection: pendingSelection,
      restoring: false,
      enhancing: null,
      enhancementScheduled: false,
      visualsReady: null,
    };
    pendingSelection = null;
    records.set(root, record);
    enhanceBottomSheet(root, record);

    record.handleClick = (event) => {
      const toggle = event.target.closest?.('[data-artbox-option-toggle]');
      if (toggle && root.contains(toggle)) {
        const option = toggle.closest('[data-swatch-option]');
        const nextState = option.dataset.artboxOptionState === 'collapsed' ? 'expanded' : 'collapsed';
        setOptionState(root, option, nextState);
        return;
      }

      const item = event.target.closest?.('[data-swatch-item]');
      const option = item?.closest?.('[data-swatch-option]');
      if (!item || !option || !root.contains(option) || record.restoring) return;

      const normalizedName = normalize(optionName(option));
      const storageKey = rememberedOptions.get(normalizedName);
      waitForKallesUpdate().then(() => {
        if (!records.has(root) || !root.isConnected) return;
        if (storageKey) {
          const selected = selectedItem(option);
          if (selected && isAvailable(selected)) {
            const value = selected.getAttribute('data-value')?.trim() || '';
            writeRememberedValue(storageKey, value);
            if (record.selection) {
              record.selection[normalizedName === 'device' ? 'device' : 'caseType'] = value;
            }
            if (option.dataset.artboxOptionState === 'required') setOptionState(root, option, 'expanded');
          }
          syncButtonRequirement(root);
        }
        syncSummaryImage(root);
        syncAddToBagLabel(root);
      });
    };

    record.handleSubmit = (event) => {
      const required = root.querySelector('[data-artbox-option-state="required"]');
      if (!required) return;
      event.preventDefault();
      required.querySelector('[data-swatch-item]:not(.is--soldout):not(.is--unavailable)')?.focus();
    };

    root.addEventListener('click', record.handleClick);
    root.addEventListener('submit', record.handleSubmit, true);

    const startEnhancement = () => {
      if (!root.classList.contains('initProducts__enabled')) return;
      record.readinessObserver?.disconnect();
      record.readinessObserver = null;
      enhanceOptions(root, record);
    };

    if (root.classList.contains('initProducts__enabled')) {
      startEnhancement();
    } else if (typeof window.MutationObserver === 'function') {
      record.readinessObserver = new window.MutationObserver(startEnhancement);
      record.readinessObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
    }

    if (typeof window.MutationObserver === 'function') {
      record.contentObserver = new window.MutationObserver((mutations) => {
        if (!root.isConnected) return;
        if (!mutations.some(mutationTouchesSwatchOptions)) return;
        scheduleEnhancement(root, record);
      });
      record.contentObserver.observe(root, { childList: true, subtree: true });
    }
  }

  function findRoots(scope) {
    const roots = [];
    if (scope.matches?.('[data-artbox-quick-shop]')) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.('[data-artbox-quick-shop]') || []));
    return roots;
  }

  function initialise(scope) {
    findRoots(scope).forEach(initialiseRoot);
  }

  function quickShopRootFromAddedNode(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    if (node.matches('[data-artbox-quick-shop]')) return node;
    if (node.closest('[data-artbox-quick-shop]')) return null;
    if (!node.matches('.t4s-modal') && !node.closest('.t4s-modal')) return null;
    return node.querySelector('[data-artbox-quick-shop]');
  }

  const lifecycle = { initialise };
  window[lifecycleKey] = lifecycle;

  document.addEventListener('pointerdown', (event) => {
    const trigger = event.target.closest?.('[data-action-quickshop], [data-artbox-quick-shop-trigger]');
    if (trigger) beginPendingState(trigger);
  }, true);
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-action-quickshop], [data-artbox-quick-shop-trigger]');
    if (trigger) {
      beginPendingState(trigger);
      return;
    }
    if (event.target.closest?.('[data-t4s-modal-close], .t4s-modal-close')) clearPendingState();
  }, true);

  if (typeof window.MutationObserver === 'function') {
    const documentObserver = new window.MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          const root = quickShopRootFromAddedNode(node);
          if (root) initialiseRoot(root);
        }
      }
    });
    documentObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(document), { once: true });
  } else {
    initialise(document);
  }
}());
